import { Button, Checkbox, Modal, TextInput } from "flowbite-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Loader from "../Common/Loader";
import { User } from "../../Models/User";
import { useAppSelector } from "../../Reducers/store";
import { UserLevelNames } from "../../Models/UserLevel";
import UserApi from "../../Api/UserApi";
import {
  createColumnHelper,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowSelectionState,
  useReactTable,
  ColumnDef,
} from "@tanstack/react-table";
import PaginatedTable from "../Common/PaginatedTable";
import { Link } from "react-router-dom";
import FemaCsvImportModal from "./FEMAImport";
import { HiMail } from "react-icons/hi";

type EditableField = "username" | "password";

export default function FEMAAppInfo() {
  const { userModel } = useAppSelector((state) => state.user);
  const [userListData, setUserListData] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [showImport, setShowImport] = useState(false);
  const [validOnly, setValidOnly] = useState(false);

  // Editing & drafts per-cell (key = `${id}:${field}`)
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [draft, setDraft] = useState<Record<string, string>>({});

  // Confirmation modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmIds, setConfirmIds] = useState<number[] | null>(null);
  const [sending, setSending] = useState(false);

  const keyFor = (id: number, field: EditableField) => `${id}:${field}`;
  const isEditing = (id: number, field: EditableField) =>
    !!editing[keyFor(id, field)];

  const beginEdit = (id: number, field: EditableField, initial: string) => {
    const key = keyFor(id, field);
    setEditing((m) => ({ ...m, [key]: true }));
    setDraft((d) => ({ ...d, [key]: initial ?? "" }));
  };

  const isValidCredentials = (u: User) =>
    !!u.femaAppUsername?.trim() && !!u.femaAppPassword?.trim();

  const displayData = useMemo(
    () => (validOnly ? userListData.filter(isValidCredentials) : userListData),
    [validOnly, userListData]
  );

  const cancelEdit = (id: number, field: EditableField) => {
    const key = keyFor(id, field);
    setEditing((m) => ({ ...m, [key]: false }));
    setDraft((d) => {
      const { [key]: _omit, ...rest } = d;
      return rest;
    });
  };

  const commitEdit = async (id: number, field: EditableField) => {
    const key = keyFor(id, field);
    const newVal = draft[key] ?? "";

    const current = userListData.find((u) => u.id === id);
    if (!current) {
      cancelEdit(id, field);
      return;
    }

    const original =
      field === "username"
        ? current.femaAppUsername ?? ""
        : current.femaAppPassword ?? "";

    if (newVal === original) {
      cancelEdit(id, field);
      return;
    }

    const patch =
      field === "username"
        ? ({ femaAppUsername: newVal } as Partial<User>)
        : ({ femaAppPassword: newVal } as Partial<User>);

    // optimistic update
    setUserListData((prev) =>
      prev.map((u) => (u.id === id ? { ...u, ...patch } : u))
    );

    try {
      await UserApi.updateFEMAFields?.(id, patch);
      toast.success("Saved");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save. Reverting.");
      void getUsers(); // rollback by refetch
    } finally {
      cancelEdit(id, field);
    }
  };

  const getUsers = async () => {
    setIsLoading(true);
    try {
      const res = await UserApi.getFEMAMemberUserList();
      const list = Array.isArray(res)
        ? res
        : Array.isArray((res as any)?.data)
        ? (res as any).data
        : [];
      setUserListData(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
      toast.error("Error loading users");
      setUserListData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void getUsers();
  }, []);

  const clearSelection = () => {
    setRowSelection({});
    setSelectedUsers([]);
  };

  const checkAdminAccess = () =>
    !!(userModel && userModel.userLevelId === UserLevelNames.SuperAdmin);

  const columnHelper = createColumnHelper<User>();

  // Helpers for send confirmation
  const openConfirmForIds = (ids: number[]) => {
    if (!ids || ids.length === 0) return;
    setConfirmIds(ids);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmIds(null);
  };

  const idToUser = (id: number) => userListData.find((u) => u.id === id);

  const isNonEmpty = (s?: string | null) => !!s && s.trim().length > 0;

  const canSend = (u: User) =>
    isNonEmpty(u.emailAddress) &&
    isNonEmpty(u.femaAppUsername) &&
    isNonEmpty(u.femaAppPassword);

  const performSend = async () => {
    if (!confirmIds || confirmIds.length === 0) return;
    try {
      setSending(true);

      if (confirmIds.length === 1) {
        // single send
        await UserApi.sendFemaEmail(confirmIds[0]);
        toast.success("Email sent");

        setUserListData((prev) =>
          prev.map((u) =>
            u.id === confirmIds[0]
              ? { ...u, femaCredsLastSentUtc: new Date().toISOString() }
              : u
          )
        );
      } else {
        // bulk send
        const { data } = await UserApi.sendFemaEmailBulk(confirmIds);
        // If your API returns a shape like { sent, skipped, errors }
        if (data && typeof data === "object") {
          const { sent, skipped, errors } = data as any;
          toast.success(
            `Sent: ${sent ?? "?"}, Skipped: ${skipped ?? "?"}${
              errors ? `, Errors: ${errors}` : ""
            }`
          );
        } else {
          toast.success("Emails sent.");
        }

        const nowIso = new Date().toISOString();
        setUserListData((prev) =>
          prev.map((u) =>
            confirmIds.includes(u.id)
              ? { ...u, femaCredsLastSentUtc: nowIso }
              : u
          )
        );
      }
    } catch (e: any) {
      toast.error(e?.response?.data ?? "Failed to send");
    } finally {
      setSending(false);
      closeConfirm();
    }
  };
  const SendTestEmail = async () => {
    try {
      setSending(true);
      const validSelected = selectedUsers.filter(canSend);

      if (validSelected.length !== 1) {
        toast.error(
          "Select exactly one user (with email, username, and password) to Send Test."
        );
        return;
      }

      const userId = validSelected[0].id;
      await UserApi.sendFemaEmailTest(userId);
      toast.success("Email sent");
    } catch (e: any) {
      toast.error(e?.response?.data ?? "Failed to send");
    } finally {
      setSending(false);
      closeConfirm();
    }
  };
  const columns = useMemo<ColumnDef<User, any>[]>(() => {
    return [
      // Selection column
      columnHelper.accessor("id", {
        header: ({ table }) => (
          <div className="flex justify-center">
            {checkAdminAccess() && (
              <Checkbox
                checked={table.getIsAllRowsSelected()}
                onChange={table.getToggleAllRowsSelectedHandler()}
                className="justify-center p-2"
              />
            )}
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center">
            {checkAdminAccess() && (
              <Checkbox
                checked={row.getIsSelected()}
                onChange={row.getToggleSelectedHandler()}
                className="self-center p-2 mr-2"
              />
            )}
          </div>
        ),
        size: 75,
        enableColumnFilter: false,
        enableSorting: false,
      }),

      // Actions column
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const u = row.original;
          const enabled = canSend(u);
          return (
            <Button
              size="xs"
              color={enabled ? "blue" : "light"}
              disabled={!enabled}
              onClick={(e) => {
                e.stopPropagation();
                openConfirmForIds([u.id]); // confirm single
              }}
            >
              <HiMail className="mr-1" /> Send
            </Button>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },

      // Derived member name
      columnHelper.accessor(
        (row) => (row.member?.name ? row.member.name : "EMDA"),
        {
          id: "memberName",
          header: "Member",
          cell: (info) => <i>{info.getValue()}</i>,
          filterFn: "includesString",
        }
      ),

      // Names as links
      columnHelper.accessor("firstName", {
        header: "First Name",
        cell: ({ row }) => (
          <Link
            to={`/user/${row.original.id}`}
            className="text-blue-600 hover:underline"
          >
            {row.original.firstName}
          </Link>
        ),
        sortingFn: "alphanumeric",
        filterFn: "includesString",
      }),
      columnHelper.accessor("lastName", {
        header: "Last Name",
        cell: ({ row }) => (
          <Link
            to={`/user/${row.original.id}`}
            className="text-blue-600 hover:underline"
          >
            {row.original.lastName}
          </Link>
        ),
        sortingFn: "alphanumeric",
        filterFn: "includesString",
      }),

      columnHelper.accessor("emailAddress", {
        header: "Email",
        sortingFn: "alphanumeric",
        filterFn: "includesString",
      }),

      // Username (edit-on-click, save on blur/Enter)
      columnHelper.accessor("femaAppUsername", {
        id: "femaUsername",
        header: "Username",
        cell: ({ row, getValue }) => {
          const id = row.original.id;
          const value = (getValue() as string) ?? "";
          const field: EditableField = "username";
          const key = keyFor(id, field);

          if (isEditing(id, field)) {
            return (
              <TextInput
                type="text"
                value={draft[key] ?? ""}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, [key]: e.target.value }))
                }
                onBlur={() => void commitEdit(id, field)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void commitEdit(id, field);
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    cancelEdit(id, field);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                autoFocus
                className="w-full"
              />
            );
          }

          return (
            <div
              role="button"
              tabIndex={0}
              className="text-left w-full truncate cursor-text"
              title="Click to edit"
              onClick={(e) => {
                e.stopPropagation();
                beginEdit(id, field, value);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  beginEdit(id, field, value);
                }
              }}
            >
              <span className="block w-full truncate text-blue-600 underline">
                {(value && value.trim()) || "Set Now"}
              </span>
            </div>
          );
        },
        enableSorting: false,
        filterFn: "includesString",
      }),

      // Password (edit-on-click, save on blur/Enter)
      columnHelper.accessor("femaAppPassword", {
        id: "femaPassword",
        header: "Password",
        cell: ({ row, getValue }) => {
          const id = row.original.id;
          const value = (getValue() as string) ?? "";
          const field: EditableField = "password";
          const key = keyFor(id, field);

          if (isEditing(id, field)) {
            return (
              <TextInput
                type="text" // change to "password" if you want masking
                value={draft[key] ?? ""}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, [key]: e.target.value }))
                }
                onBlur={() => void commitEdit(id, field)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void commitEdit(id, field);
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    cancelEdit(id, field);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                autoFocus
                className="w-full"
              />
            );
          }

          return (
            <div
              role="button"
              tabIndex={0}
              className="text-left w-full truncate cursor-text"
              title="Click to edit"
              onClick={(e) => {
                e.stopPropagation();
                beginEdit(id, field, value);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  beginEdit(id, field, value);
                }
              }}
            >
              <span className="block w-full truncate text-blue-600 underline">
                {(value && value.trim()) || "Set Now"}
              </span>
            </div>
          );
        },
        enableSorting: false,
        filterFn: "includesString",
      }),

      columnHelper.accessor("femaCredsLastSentUtc" as any, {
        id: "lastSent",
        header: "Last Sent",
        cell: ({ getValue }) => {
          const v = getValue() as string | null | undefined;
          return v ? (
            new Date(v).toLocaleString()
          ) : (
            <span className="text-gray-400">—</span>
          );
        },
        enableSorting: true,
      }),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, draft, userListData]);

  const table = useReactTable<User>({
    data: displayData,
    columns,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 20 },
      sorting: [{ id: "lastName", desc: false }],
    },
    getRowId: (row) => String(row.id),
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    state: { rowSelection },
    enableMultiRowSelection: true,
    onRowSelectionChange: (updater) => {
      setRowSelection((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        const selected = Object.keys(next)
          .filter((rowId) => (next as any)[rowId])
          .map((rowId) => table.getRow(rowId)?.original)
          .filter(Boolean) as User[];
        setSelectedUsers(selected);
        return next;
      });
    },
  });

  return (
    <div className="w-full">
      <div className="w-full p-4">
        <div className="mx-1 flex items-end">
          <div className="flex flex-1">
            <h1 className="text-gray-900 font-bold text-4xl">FEMA User List</h1>
          </div>

          <div className="p-3">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={validOnly}
                onChange={(e) => {
                  const checked = e.currentTarget.checked;
                  setValidOnly(checked);
                  setRowSelection({});
                  table.setPageIndex(0);
                }}
              />
              Only valid username + password
            </label>
          </div>

          {checkAdminAccess() && selectedUsers.length > 0 && (
            <div className="flex gap-2">
              <Button
                color="blue"
                className="mr-2"
                disabled={selectedUsers.filter(canSend).length === 0}
                onClick={() => {
                  const valid = selectedUsers.filter(canSend);
                  if (valid.length === 0) {
                    toast.error(
                      "No selected users have both username and password."
                    );
                    return;
                  }
                  openConfirmForIds(valid.map((u) => u.id));
                }}
              >
                Send to Selected
              </Button>
              <Button onClick={clearSelection} className="buttonMain">
                Clear Selection
              </Button>
            </div>
          )}
        </div>

        {/* Tools row: Import + Send Test on the same line */}
        <div className="m-4 text-gray-600 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => setShowImport(true)} className="buttonMain">
              Import CSV
            </Button>
            <Button onClick={() => SendTestEmail()} className="buttonMain">
              Send Test
            </Button>
          </div>

          {/* Keep the modal nearby so it's mounted */}
          <FemaCsvImportModal
            open={showImport}
            onClose={() => setShowImport(false)}
            onDone={getUsers}
          />
        </div>
      </div>

      <div className="w-full p-4">
        {isLoading && <Loader />}
        <div className="w-full mx-auto overflow-x-auto">
          <PaginatedTable
            table={table}
            dataCount={userListData.length}
            columnResizeMode="onChange"
            showFilters={true}
          />
        </div>
      </div>

      {/* CONFIRMATION MODAL */}
      <Modal show={!!confirmOpen} size="md" onClose={closeConfirm} popup>
        <Modal.Header />
        <Modal.Body>
          <div className="text-center p-4">
            <h3 className="mb-2 text-lg font-semibold">
              {confirmIds?.length === 1
                ? "Send credentials to this user?"
                : `Send credentials to ${confirmIds?.length ?? 0} users?`}
            </h3>

            {confirmIds && (
              <div className="text-sm text-gray-700 max-h-40 overflow-y-auto mt-2">
                {confirmIds.slice(0, 6).map((id) => {
                  const u = idToUser(id);
                  return (
                    <div key={id} className="truncate">
                      {u
                        ? `${u.firstName ?? ""} ${u.lastName ?? ""} (${
                            u.emailAddress ?? "no email"
                          })`
                        : `User #${id}`}
                    </div>
                  );
                })}
                {confirmIds.length > 6 && (
                  <div className="text-gray-500 mt-1">
                    …and {confirmIds.length - 6} more
                  </div>
                )}
              </div>
            )}

            <p className="text-xs text-gray-500 mt-3">
              This will email FEMA app credentials to the selected recipient(s).
            </p>

            <div className="mt-5 flex justify-center gap-3">
              <Button onClick={performSend} disabled={sending}>
                {sending ? "Sending..." : "Send"}
              </Button>
              <Button color="gray" onClick={closeConfirm} disabled={sending}>
                Cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
